import { FC } from 'react'
import gql from 'graphql-tag'
import styled from 'styled-components'
import { CoverLetterListItemPc_CoverLetterFragment } from 'generated/graphql'
import { COVER_LETTER_TYPE_TEXT_OBJECT } from '@app.feature/coverletter/constant'
import Link, { LinkProps } from 'components/Link'
import useKeywordHighlighter from 'hooks/useKeywordHighlighter'
import { getPassedAtText, getSpecInfo } from 'utils/coverLetter'
import { useSearchStateContext } from '../../../useSearchState'
import CoverLetterListItemPContent from './CoverLetterListItemPContent'

export interface CoverLetterListItemProps {
  coverLetter?: CoverLetterListItemPc_CoverLetterFragment | null
  LinkProps: LinkProps
  coverLetterHighlight?: string[] | null
}

const CoverLetterListItemPC: FC<CoverLetterListItemProps> = ({
  coverLetter,
  LinkProps,
  coverLetterHighlight,
}) => {
  const { search } = useSearchStateContext()
  const highlightedOrgName = useKeywordHighlighter({
    text: coverLetter?.organizationName || '-',
    keyword: search.organizationName,
  })

  const highlightedRole = useKeywordHighlighter({
    text: coverLetter?.role || '-',
    keyword: search.role,
  })

  const typesText = coverLetter?.types
    ?.map((type) => {
      if (!type) {
        return ''
      }
      return `${COVER_LETTER_TYPE_TEXT_OBJECT[type]}`
    })
    .join(', ')

  return (
    <StyledWrapper>
      <Link className="link" {...LinkProps}>
        <div className="flex-wrapper">
          {/* 상반기: 1~6월, 하반기: 7~12월 */}
          <p className="coverletter-content passed-at">
            {getPassedAtText(coverLetter)}
          </p>
          <p
            className="coverletter-content ellipsis organization-name"
            dangerouslySetInnerHTML={{
              __html: highlightedOrgName,
            }}
          />
          <p
            className="coverletter-content ellipsis role"
            dangerouslySetInnerHTML={{
              __html: highlightedRole,
            }}
          />
          <p className="coverletter-content type-text">{typesText || '-'}</p>
          <div className="content-wrapper">
            <p className="spec">{getSpecInfo(coverLetter)}</p>
            <CoverLetterListItemPContent
              content={coverLetter?.contentSummaryPC || ''}
              highlight={coverLetterHighlight || []}
              typographyProps={{
                className:
                  'coverletter-content content-ellipsis content-highlight',
              }}
            />
          </div>
        </div>
      </Link>
    </StyledWrapper>
  )
}

export default CoverLetterListItemPC

const StyledWrapper = styled.div`
  .flex-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .passed-at {
    margin-left: 10px;
    margin-right: 20px;
    width: 100px;
    text-align: center !important;
  }

  .coverletter-content {
    color: #363636;
    text-align: start;
    min-width: 100px;
    font-size: 16px;
    font-weight: normal;
    margin-bottom: 0px;
  }

  .type-text {
    margin-right: 36px;
    text-align: center;
    width: 100px;
    word-wrap: break-word;
  }

  .content-highlight {
    & b {
      background-color: #bbe6ff;
    }
  }

  .organization-name,
  .role {
    margin-right: 20px;
    text-align: center;
    width: 100px;

    & .highlight {
      font-weight: 600;
    }
  }

  .content-wrapper {
    margin-right: 32px;
    flex-grow: 1;
    display: block;
    min-width: 0;
    max-width: calc(100% - 416px);
    flex-direction: row;
    letter-spacing: -0.64px;
  }

  .spec {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 10px;
    line-height: 24px;
    max-height: 24px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }

  .ellipsis {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .content-ellipsis {
    line-height: 24px;
    max-height: 72px; /* 24 * 3 */
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  .link {
    &:hover {
      text-decoration: underline;
    }
  }
`

gql`
  fragment CoverLetterListItemPC_coverLetter on CoverLetter {
    id
    role
    contentSummaryPC: content(length: 190)
    organizationName
    types
    ...CoverLetterUtils_getSpecInfo_coverLetter
    ...CoverLetterUtils_getPassedAtText_coverLetter
  }
`
